import type { ReactNode } from "react";
import { UserIcon } from "../../../../assets/icon/svg";
import ProfileTab from "../../account-setting/profile-tab";
import { DepartmentUser } from "../../../../api/user/user-api-state";

interface ProfileViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number | null;
    getDepartmentUserById: (userId: number) => DepartmentUser | undefined;
}

const InfoItem = ({ label, value }: { label: string; value?: string | number | null }) => (
    <div className="min-w-0 rounded-lg border border-gray-100 bg-white px-3 py-3 transition hover:border-[#186cb2]/20">
        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
        <p className="mt-1.5 break-words text-xs font-semibold text-gray-700 sm:text-sm">
            {value !== null && value !== undefined && String(value).trim() !== "" ? value : "—"}
        </p>
    </div>
);

const InfoSection = ({ title, children }: { title: string; children: ReactNode }) => (
    <section>
        <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-700 sm:text-sm">
            <span className="h-4 w-1 rounded-full bg-[#186cb2]" />
            {title}
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {children}
        </div>
    </section>
);

export default function ProfileViewModal({ isOpen, onClose, getDepartmentUserById, userId }: ProfileViewModalProps) {

    const userData = userId !== null ? getDepartmentUserById(userId) : undefined;
    if (!isOpen || !userData) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 backdrop-blur-[2px] sm:p-4">
            <div className="relative w-full flex h-full max-h-[92dvh] flex-col overflow-hidden rounded-xl border border-gray-200 bg-[#f5f9fd] shadow-2xl sm:max-h-[90vh] sm:max-w-[95vw] lg:max-w-[700px] xl:max-w-[800px] 2xl:max-w-[1000px]">
                {/* HEADER */}
                <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3 sm:px-5">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">
                            {'Employee Profile' }
                        </h2>
                        <p className="mt-0.5 text-xs text-gray-400">
                            {'View employee information and manage account settings.' }
                        </p>
                    </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="space-y-4 p-3 sm:p-4">
                        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                            <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                                <div className="flex items-start gap-2.5">
                                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                        <UserIcon width={15} height={15} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-800">
                                            Personal Information
                                        </p>
                                        <p className="mt-0.5 text-[10px] text-gray-400">
                                            Employee personal and contact information.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
                                <ProfileTab
                                    labelName="First Name"
                                    labelData={userData.firstname ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Middle Name"
                                    labelData={userData.middlename ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Last Name"
                                    labelData={userData.lastname ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Suffix"
                                    labelData={userData.suffix ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Phone Number"
                                    labelData={userData.phoneNumber ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Email Address"
                                    labelData={userData.email ?? '-'}
                                />
                            </div>
                        </section>
                        {/* ACCOUNT INFORMATION */}
                        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                            <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                                <div className="flex items-start gap-2.5">
                                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h10" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-800">
                                            Account Information
                                        </p>
                                        <p className="mt-0.5 text-[10px] text-gray-400">
                                            Employee role, work assignment, and account details.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
                                <ProfileTab
                                    labelName="Position"
                                    labelData={userData.position ?? '-'}
                                />
                                {/* <ProfileTab
                                    labelName="Role"
                                    labelData={userData.role_name ?? '-'}
                                /> */}
                                <ProfileTab
                                    labelName="Department"
                                    labelData={userData.department_name ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Location"
                                    labelData={userData.location_name ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Agent Code"
                                    labelData={userData.agent_code ?? '-'}
                                />
                                <ProfileTab
                                    labelName="Reporting Head"
                                    labelData={userData.reporting_to_name ?? '-'}
                                />
                            </div>
                        </section>
                    </div>
                </div>
                <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] sm:px-4">
                    <section className="overflow-hidden bg-white">
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-[#186cb2]/30 hover:bg-[#186cb2]/5 hover:text-[#186cb2] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                                    <path d="M18 6 6 18" />
                                    <path d="m6 6 12 12" />
                                </svg>
                                <span>
                                    Close
                                </span>
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
        // <div>
        //     className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 p-3 backdrop-blur-[2px] sm:p-5"
        //     onMouseDown={(e) => {
        //         if (e.target === e.currentTarget) onClose();
        //     }}
        // >
        //     <div
        //         role="dialog"
        //         aria-modal="true"
        //         aria-labelledby="team-member-modal-title"
        //         className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        //     >
        //         {/* Header */}
        //         <div className="flex shrink-0 items-center justify-between border-b border-[#186cb2]/15 bg-[#eef6fc] px-4 py-3 sm:px-5">
        //             <div className="flex min-w-0 items-center gap-3">
        //                 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#186cb2] text-white">
        //                     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
        //                         <circle cx="12" cy="8" r="4" />
        //                         <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
        //                     </svg>
        //                 </div>
        //                 <div className="min-w-0">
        //                     <h2 id="team-member-modal-title" className="text-sm font-semibold text-gray-800 sm:text-base">
        //                         Team Member Details
        //                     </h2>
        //                     <p className="mt-0.5 text-[10px] text-gray-500 sm:text-xs">
        //                         Personal, contact, and work information
        //                     </p>
        //                 </div>
        //             </div>
        //             <button
        //                 type="button"
        //                 onClick={onClose}
        //                 aria-label="Close modal"
        //                 className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-white hover:text-gray-700"
        //             >
        //                 <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
        //                     <path d="m18 6-12 12M6 6l12 12" />
        //                 </svg>
        //             </button>
        //         </div>

        //         {/* Scrollable Details */}
        //         <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
        //             {/* Profile Summary */}
        //             <div className="flex flex-col gap-3 rounded-lg border border-[#186cb2]/15 bg-[#f8fbfe] p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
        //                 <div className="flex min-w-0 items-center gap-3">
        //                     <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#186cb2]/10 text-sm font-bold text-[#186cb2] ring-1 ring-[#186cb2]/15">
        //                         {initials || "TM"}
        //                     </div>
        //                     <div className="min-w-0">
        //                         <p className="break-words text-sm font-semibold text-gray-800 sm:text-base">
        //                             {user.name}
        //                         </p>
        //                         <p className="mt-1 text-xs text-gray-500">
        //                             {user.position || user.role_name}
        //                         </p>
        //                         <p className="mt-1 break-all text-xs text-gray-500">{user.email}</p>
        //                     </div>
        //                 </div>
        //                 <span className={`inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${user.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
        //                     <span className={`h-1.5 w-1.5 rounded-full ${user.is_active ? "bg-green-500" : "bg-gray-400"}`} />
        //                     {user.is_active ? "Active" : "Inactive"}
        //                 </span>
        //             </div>

        //             <InfoSection title="Personal Information">
        //                 <InfoItem label="First Name" value={user.fistname} />
        //                 <InfoItem label="Middle Name" value={user.middlename} />
        //                 <InfoItem label="Last Name" value={user.lastname} />
        //                 <InfoItem label="Suffix" value={user.suffix} />
        //             </InfoSection>

        //             <InfoSection title="Contact Information">
        //                 <InfoItem label="Email Address" value={user.email} />
        //                 <InfoItem label="Contact Number" value={user.contactnumber} />
        //                 <InfoItem label="Assigned Location" value={user.location} />
        //             </InfoSection>

        //             <InfoSection title="Work Information">
        //                 <InfoItem label="Department" value={user.department_name} />
        //                 <InfoItem label="Role" value={user.role_name} />
        //                 <InfoItem label="Position" value={user.position} />
        //                 <InfoItem label="Agent Code" value={user.agentCode} />
        //                 <InfoItem label="Reporting Head" value={user.reportingheader} />
        //             </InfoSection>
        //         </div>

        //         {/* Footer */}
        //         <div className="flex shrink-0 justify-end border-t border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
        //             <button
        //                 type="button"
        //                 onClick={onClose}
        //                 className="h-8 rounded-md bg-[#186cb2] px-4 text-xs font-semibold text-white transition hover:bg-[#145d9a] active:scale-95"
        //             >
        //                 Close
        //             </button>
        //         </div>
        //     </div>
        // </div>
    );
}